import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import Icon from './AppIcon';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

const MioProfilo = () => {
  const { t, i18n } = useTranslation();
  const [profilo, setProfilo] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate(); 

  useEffect(() => {
    fetchMioProfilo();
  }, []);

  const fetchMioProfilo = async () => {
    try {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      
      if (user) {
        const { data, error } = await supabase
          .from('profili')
          .select('*')
          .eq('id', user.id)
          .single();

        if (error) throw error;
        setProfilo(data);
      }
    } catch (err) {
      console.error("Errore fetch profilo:", err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center p-20 text-primary">
      <div className="animate-spin mr-3"><Icon name="Loader2" size={24} /></div>
      <span className="text-sm font-bold uppercase tracking-widest">{t('profile.loading')}</span>
    </div>
  );

  if (!profilo) return <div className="p-10 text-center font-bold">{t('profile.no_data')}</div>;

  const infoRows = [
    { label: t('profile.full_name'), value: `${profilo.nome} ${profilo.cognome}`, icon: 'User' },
    { label: t('profile.email'), value: profilo.email, icon: 'Mail' },
    { label: t('profile.phone'), value: profilo.phone || t('profile.not_specified'), icon: 'Phone' },
    { label: t('profile.cf'), value: profilo.cf || 'N/D', icon: 'Fingerprint' },
    { label: t('profile.role'), value: profilo.ruolo || t('profile.employee'), icon: 'Briefcase' },
    { 
      label: t('profile.reg_date'), 
      value: new Date(profilo.creato_il).toLocaleDateString(i18n.language),
      icon: 'Calendar' 
    },
    { 
      label: t('profile.last_access'), 
      value: profilo.ultimo_accesso ? new Date(profilo.ultimo_accesso).toLocaleString(i18n.language) : t('profile.first_session'), 
      icon: 'LogIn' 
    },
  ];

  return (
    <div className="max-w-4xl mx-auto space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
      
      {/* Profil Card Header */}
      <div className="bg-card border border-border rounded-3xl p-8 shadow-sm">
        <div className="flex flex-col md:flex-row items-center gap-6">
          <div className="w-24 h-24 rounded-2xl bg-primary/10 flex items-center justify-center text-primary font-bold text-3xl border border-primary/20 shadow-inner shrink-0">
            {profilo.nome?.[0]}{profilo.cognome?.[0]}
          </div>
          
          <div className="text-center md:text-left flex-1 space-y-3">
            <div>
              <h1 className="text-3xl font-black text-foreground tracking-tight leading-none">
                {profilo.nome} {profilo.cognome}
              </h1>
              <p className="text-muted-foreground font-bold mt-1 uppercase text-xs tracking-widest opacity-70">
                {profilo.email}
              </p>
            </div>

            <div className="flex flex-wrap justify-center md:justify-start gap-2">
              <span className="px-3 py-1 bg-primary text-white text-[10px] font-black uppercase rounded-lg tracking-widest">
                {profilo.ruolo || t('profile.employee')}
              </span>
            </div>
          </div>
        </div>

        {/* Donji dio headera sa ID-om i Dugmetom za lozinku */}
        <div className="mt-8 pt-6 border-t border-border/50 flex flex-col md:flex-row gap-4 items-end justify-between">
          <div className="flex flex-col gap-1.5 w-full md:flex-1">
            <span className="text-[10px] font-black uppercase text-muted-foreground tracking-widest">
              {t('profile.unique_id')}
            </span>
            <code className="bg-muted/50 border border-border p-3 rounded-xl text-[11px] font-mono text-primary break-all">
              {profilo.id}
            </code>
          </div>

          <button 
            onClick={() => navigate('/change-password')}
            className="w-full md:w-auto flex items-center justify-center gap-2 bg-primary/10 hover:bg-primary/20 text-primary px-6 py-3 rounded-2xl transition-all font-black uppercase text-[10px] tracking-widest group shadow-sm"
          >
            <div className="group-hover:rotate-12 transition-transform">
              <Icon name="Key" size={16} />
            </div>
            {t('profile.change_password') || 'Cambia Password'}
          </button>
        </div>
      </div>

      {/* Dettagli Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {infoRows.map((item, idx) => (
          <div key={idx} className="bg-card border border-border rounded-2xl p-5 hover:border-primary/40 transition-all group shadow-sm">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-1.5 bg-muted rounded-lg text-primary/60 group-hover:text-primary transition-colors">
                <Icon name={item.icon} size={14} />
              </div>
              <span className="text-[10px] font-black uppercase text-muted-foreground tracking-widest">
                {item.label}
              </span>
            </div>
            <p className="text-sm font-bold text-foreground">
              {item.value}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};

export default MioProfilo;